import { useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import { useSequenciaDoCliente } from '../../shared/api/queries.ts';
import type { Cliente } from '../../shared/api/tipos.ts';
import { formatarData, formatarInteiro } from '../../shared/format.ts';
import { SeletorDeCliente } from './SeletorDeCliente.tsx';

/** O período digitado, que só vira consulta no Conferir. */
interface Periodo {
  readonly de: string;
  readonly ate: string;
}

/**
 * Os números que faltam na numeração das notas que a loja emitiu.
 *
 * O PDV numera em sequência, então um vão entre a 1.204 e a 1.210 é sintoma
 * de nota que não chegou: máquina que passou dias sem o agente, XML apagado
 * da pasta, caixa que trabalhou offline. Nenhuma outra tela mostra isso —
 * elas listam o que existe, e aqui o que importa é o que não está lá.
 *
 * A tela relata e não acusa: numeração inutilizada na SEFAZ também deixa vão,
 * e quem sabe diferenciar as duas coisas é quem conhece a loja.
 */
export function QuebrasDeSequencia({
  clientes,
  carregandoClientes,
  empresaId,
  aoEscolherCliente,
  periodoSugerido,
}: {
  clientes: readonly Cliente[];
  carregandoClientes: boolean;
  empresaId: string | null;
  aoEscolherCliente: (empresaId: string | null) => void;
  /** O mês escolhido nas abas, com que a conferência abre. */
  periodoSugerido: Periodo;
}) {
  const [periodo, setPeriodo] = useState<Periodo>(periodoSugerido);
  // O cliente aplica na hora — é uma escolha, não algo digitado. As datas
  // esperam o Conferir, como em arquivos fiscais: cada tecla de uma data
  // dispararia uma varredura do acervo inteiro da loja.
  const [aplicado, setAplicado] = useState<Periodo>(periodoSugerido);

  const { data, isPending, isFetching, isError, error } = useSequenciaDoCliente({
    empresaId: empresaId ?? undefined,
    de: aplicado.de === '' ? undefined : aplicado.de,
    ate: aplicado.ate === '' ? undefined : aplicado.ate,
  });

  const quebras = data?.quebras ?? [];
  const periodoPendente = periodo.de !== aplicado.de || periodo.ate !== aplicado.ate;

  function conferir(evento?: FormEvent) {
    evento?.preventDefault();
    setAplicado(periodo);
  }

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent component="form" onSubmit={conferir}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ alignItems: { md: 'flex-start' } }}
          >
            <SeletorDeCliente
              clientes={clientes}
              carregando={carregandoClientes}
              empresaId={empresaId}
              aoEscolher={aoEscolherCliente}
              ajuda="A numeração é de quem emite, então a conferência é de uma loja por vez."
            />

            <TextField
              label="De"
              type="date"
              value={periodo.de}
              onChange={(evento) => setPeriodo((atual) => ({ ...atual, de: evento.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="Em branco: o acervo inteiro da loja."
            />
            <TextField
              label="Até"
              type="date"
              value={periodo.ate}
              onChange={(evento) => setPeriodo((atual) => ({ ...atual, ate: evento.target.value }))}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <Button
              type="submit"
              variant="contained"
              startIcon={<SearchOutlinedIcon />}
              loading={isFetching}
              sx={{ mt: { md: 1 } }}
            >
              Conferir
            </Button>
          </Stack>

          {periodoPendente && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
              O período digitado ainda não foi conferido — clique em Conferir.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {empresaId === null ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              Escolha um cliente. A sequência é por emitente: cada loja numera as notas dela,
              e comparar duas numerações diferentes inventaria buracos que não existem.
            </Typography>
          ) : isError ? (
            <Alert severity="error">
              Não foi possível conferir a sequência.{' '}
              {error instanceof Error ? error.message : ''}
            </Alert>
          ) : isPending ? (
            <Stack spacing={1}>
              {Array.from({ length: 3 }, (_, indice) => (
                <Skeleton key={indice} variant="rounded" height={44} />
              ))}
            </Stack>
          ) : quebras.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
              Nenhum número faltando em {formatarInteiro(data?.analisadas ?? 0)} nota(s)
              emitida(s) por este cliente. A numeração está inteira no recorte conferido.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Documento</TableCell>
                    <TableCell align="right">Série</TableCell>
                    <TableCell>Números que faltam</TableCell>
                    <TableCell align="right">Quantidade</TableCell>
                    <TableCell>Entre as emissões de</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {quebras.map((quebra) => (
                    <TableRow
                      key={`${quebra.modelo}-${quebra.serie}-${quebra.primeiroNumero}`}
                      hover
                    >
                      <TableCell>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={quebra.modelo === '65' ? 'NFC-e' : 'NF-e'}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {quebra.serie}
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {/* Número cru, sem ponto de milhar: é assim que ele é
                            digitado na busca de arquivos fiscais e impresso
                            no cupom. */}
                        {quebra.primeiroNumero === quebra.ultimoNumero
                          ? quebra.primeiroNumero
                          : `${quebra.primeiroNumero} a ${quebra.ultimoNumero}`}
                      </TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          color="warning"
                          label={formatarInteiro(quebra.quantidade)}
                        />
                      </TableCell>
                      {/* As notas que ladeiam o buraco datam a procura: é por
                          elas que se sabe em que dia e em que turno olhar. */}
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatarData(quebra.anteriorEm)} e {formatarData(quebra.posteriorEm)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {data !== undefined && quebras.length > 0 && (
        <Alert severity="warning">
          <strong>{formatarInteiro(data.faltando)}</strong> número(s) faltando em{' '}
          <strong>{formatarInteiro(data.analisadas)}</strong> nota(s) emitida(s) por{' '}
          {data.razaoSocial}.{' '}
          {/* A lista tem teto no servidor; os dois números acima não. Dizer
              quantas quebras ficaram de fora evita a leitura de que a tabela
              é a conta inteira. */}
          {data.totalDeQuebras > quebras.length && (
            <>
              A tabela mostra as {formatarInteiro(quebras.length)} primeiras de{' '}
              {formatarInteiro(data.totalDeQuebras)} quebras — conferir mês a mês mostra o
              resto.{' '}
            </>
          )}
          Nem todo vão é nota perdida: numeração inutilizada na SEFAZ também deixa buraco, e
          período que o escritório nunca recebeu aparece aqui como uma quebra enorme entre
          dois meses. Para o que faltou mesmo, o caminho é pedir o reenvio do período à
          instalação da loja, em Clientes.
        </Alert>
      )}
    </Stack>
  );
}
